import { useEffect, useMemo, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";
import { api, type AgendamentoApi, type NaoConformidadeApi, type VistoriaResumoApi } from "../api";
import { Layout } from "../componentes/Layout";
import { IconeAvisoTriangulo, IconeCalendario, IconeCirculoCheck, IconePrancheta } from "../componentes/Icones";
import { PillStatusAgendamento, PillStatusVistoria } from "../componentes/Pill";
import { useSessao } from "../estado/sessao";

export function Dashboard() {
  const { usuario } = useSessao();
  const [vistorias, setVistorias] = useState<VistoriaResumoApi[] | null>(null);
  const [agendamentos, setAgendamentos] = useState<AgendamentoApi[] | null>(null);
  const [ocorrencias, setOcorrencias] = useState<NaoConformidadeApi[] | null>(null);

  useEffect(() => {
    const hoje = new Date();
    const daqui7dias = new Date(hoje.getTime() + 7 * 86400000);
    void Promise.all([
      api.listarVistorias().then((r) => setVistorias(r.vistorias)),
      api.listarAgendamentos(hoje.toISOString().slice(0, 10), daqui7dias.toISOString().slice(0, 10)).then((r) => setAgendamentos(r.agendamentos)),
      api.listarNaoConformidades("ABERTA").then((r) => setOcorrencias(r.naoConformidades)),
    ]).catch(() => { setVistorias((v) => v ?? []); setAgendamentos((v) => v ?? []); setOcorrencias((v) => v ?? []); });
  }, []);

  const emAndamento = useMemo(() => (vistorias ?? []).filter((v) => v.status === "EM_ANDAMENTO"), [vistorias]);
  const proximos = useMemo(() => (agendamentos ?? []).filter((a) => a.status !== "CANCELADO").sort((a, b) => a.dataHora.localeCompare(b.dataHora)), [agendamentos]);
  const tarefasAbertas = (ocorrencias ?? []).flatMap((o) => o.tarefas).filter((t) => t.status === "AGUARDANDO_ATRIBUICAO" || t.status === "PENDENTE" || t.status === "EM_ANDAMENTO" || t.status === "AGUARDANDO_VERIFICACAO");
  const carregando = vistorias === null || agendamentos === null || ocorrencias === null;

  return <Layout><main className="dashboard-shell">
    <header className="dashboard-cabecalho">
      <div><span className="dashboard-saudacao">VISÃO OPERACIONAL</span><h1>Dashboard</h1><p>{usuario?.organizacaoNome} · atualização em tempo real</p></div>
      <Link className="botao botao-primario dashboard-acao" to="/vistorias">Ver vistorias</Link>
    </header>

    <section className="dashboard-metricas" aria-label="Resumo operacional">
      <Metrica cor="azul" icone={<IconePrancheta tamanho={22} cor="currentColor" />} valor={carregando ? "—" : emAndamento.length} rotulo="Vistorias em andamento" detalhe={`${vistorias?.length ?? 0} no total`} />
      <Metrica cor="vermelho" icone={<IconeAvisoTriangulo tamanho={22} cor="currentColor" />} valor={carregando ? "—" : ocorrencias?.length ?? 0} rotulo="Não conformidades" detalhe="aguardando ação" />
      <Metrica cor="ambar" icone={<IconePrancheta tamanho={22} cor="currentColor" />} valor={carregando ? "—" : tarefasAbertas.length} rotulo="Tarefas abertas" detalhe="correções pendentes" />
      <Metrica cor="verde" icone={<IconeCalendario tamanho={22} cor="currentColor" />} valor={carregando ? "—" : proximos.length} rotulo="Agenda (7 dias)" detalhe="próximas visitas" />
    </section>

    <div className="dashboard-grade">
      <section className="painel-operacional dashboard-vistorias">
        <CabecalhoPainel titulo="Vistorias em andamento" total={emAndamento.length} rota="/vistorias" />
        <div className="dashboard-lista">
          {emAndamento.length === 0 ? <EstadoVazio texto="Nenhuma vistoria em andamento." /> : emAndamento.slice(0, 6).map((v) =>
            <Link className="dashboard-linha" to={`/vistorias/${v.id}`} key={v.id}>
              <span className="dashboard-linha-icone azul"><IconePrancheta tamanho={18} cor="currentColor" /></span>
              <span className="dashboard-linha-corpo"><small>VISTORIA</small><strong>{v.cliente.nome}</strong><span>{v.atribuidoA.nome} · {formatarData(v.dataVistoria)}</span></span>
              <PillStatusVistoria status={v.status} />
              <span className="dashboard-seta">›</span>
            </Link>)}
        </div>
      </section>

      <aside className="dashboard-coluna">
        <section className="painel-operacional resumo-operacional">
          <h2>Resumo de pendências</h2>
          <div className="resumo-donut" style={{ "--abertas": Math.min(100, (ocorrencias?.length ?? 0) * 9 + 18) } as CSSProperties}>
            <div><strong>{ocorrencias?.length ?? 0}</strong><span>em aberto</span></div>
          </div>
          <div className="resumo-legenda"><span><i className="vermelho" />Ocorrências <strong>{ocorrencias?.length ?? 0}</strong></span><span><i className="ambar" />Tarefas <strong>{tarefasAbertas.length}</strong></span></div>
          <Link to="/vistorias">Ver vistorias</Link>
        </section>
        <section className="painel-operacional dashboard-ocorrencias">
          <CabecalhoPainel titulo="Apontamentos recentes" total={ocorrencias?.length ?? 0} rota="/vistorias" />
          {(ocorrencias ?? []).slice(0, 3).map((o) => <Link to="/vistorias" className="ocorrencia-mini" key={o.id}><strong>{o.secaoTitulo}</strong><span>{o.itemRotulo}</span><small>{o.cliente.nome}</small></Link>)}
          {!carregando && ocorrencias?.length === 0 && <EstadoVazio texto="Nenhuma não conformidade aberta." />}
        </section>
      </aside>
    </div>

    <section className="painel-operacional dashboard-agenda">
      <CabecalhoPainel titulo="Agenda dos próximos 7 dias" total={proximos.length} rota="/agenda" />
      <div className="agenda-grade">{proximos.length === 0 ? <EstadoVazio texto="Nenhuma visita agendada para os próximos dias." /> : proximos.slice(0, 4).map((a) =>
        <Link to="/agenda" className="agenda-card" key={a.id}><span className="agenda-data"><strong>{new Date(a.dataHora).toLocaleDateString("pt-BR", { day: "2-digit" })}</strong><small>{new Date(a.dataHora).toLocaleDateString("pt-BR", { month: "short" }).replace(".", "")}</small></span><span><small>INSPEÇÃO</small><strong>{a.cliente.nome}</strong><em>{a.vistoriador.nome}</em></span><PillStatusAgendamento status={a.status} /></Link>)}
      </div>
    </section>
  </main></Layout>;
}

function Metrica({ cor, icone, valor, rotulo, detalhe }: { cor: string; icone: ReactNode; valor: string | number; rotulo: string; detalhe: string }) {
  return <article className="metrica"><span className={`metrica-icone ${cor}`}>{icone}</span><span><small>{rotulo}</small><strong>{valor}</strong><em>{detalhe}</em></span></article>;
}
function CabecalhoPainel({ titulo, total, rota }: { titulo: string; total: number; rota: string }) { return <header className="painel-cabecalho"><h2>{titulo} <span>{total}</span></h2><Link to={rota}>Visualizar tudo</Link></header>; }
function EstadoVazio({ texto }: { texto: string }) { return <div className="dashboard-vazio"><IconeCirculoCheck tamanho={24} cor="var(--pill-verde-texto)" selecionado /><span>{texto}</span></div>; }
function formatarData(data: string) { return new Date(data).toLocaleDateString("pt-BR", { timeZone: "UTC" }); }
